import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import cn from 'classnames'
import moment from 'moment'
import answerModel from 'components/answers/answerModel'
import AnswerButtons from 'components/answers/AnswerButtons'
import FileEmbed from 'components/answers/FileEmbed'
import { extractUrls, LinkPreview } from 'components/shared/LinkPreview'
import UserIcon from 'components/users/UserIcon'
import { getText } from 'lib/value'
import useCachedAnswer from 'refactor/hooks/api/useCachedAnswer'
import * as cache from 'state/cache'
import {
    claimMediaPlayback,
    createMediaPlaybackOwnerId,
    getMediaPlaybackState,
    releaseMediaPlayback,
    setMediaPlaybackState,
    subscribeToMediaPlaybackState,
    syncMediaPlaybackOwner,
    unregisterMediaPlaybackOwner,
} from 'state/mediaPlaybackCoordinator'
import ProgressContext from '../stories/contexts/Progress'
import { getAnswerPlayMedia, isTrackedPlayMedia } from './playStoryMedia'
import {
    getMediaBackdropUrl,
    getReactionPickerCloseBehavior,
    getReactionPickerOpenBehavior,
    getStoryControlResumeToken,
    getTrackedAudioResumePlaybackToken,
    getTrackedMediaKind,
    isTrackedAudioMediaKind,
    stripEmbeddedLinksFromCaption,
} from './playStorySlideHelpers'
import styles from './play-story-slide.module.scss'

const formatStoryTime = createdAt => {
    if (!createdAt) return ''

    const createdMoment = moment(createdAt)
    if (!createdMoment.isValid()) return ''

    return createdMoment.fromNow()
}

const getSafePlaybackTime = (mediaEl, fallback = 0) => {
    if (!mediaEl) return fallback
    return Number.isFinite(mediaEl.currentTime) && mediaEl.currentTime >= 0
        ? mediaEl.currentTime
        : fallback
}

const getAnswerVideoMediaKey = (answerId, mp4Src, src) => {
    const sourceKey = mp4Src || src
    if (!answerId || !sourceKey) return null
    return `answer-video:${String(answerId)}:${sourceKey}`
}

function usePlaySlideVideoPlayback(answerId, src, mp4Src, videoRef, persistPlayback = false) {
    const ownerIdRef = useRef(createMediaPlaybackOwnerId('play-slide-video'))
    const mediaKey = useMemo(
        () => getAnswerVideoMediaKey(answerId, mp4Src, src),
        [answerId, mp4Src, src],
    )

    useEffect(() => {
        syncMediaPlaybackOwner(ownerIdRef.current, mediaKey, videoRef.current)
    }, [mediaKey, videoRef])

    useEffect(() => {
        return () => {
            unregisterMediaPlaybackOwner(ownerIdRef.current)
        }
    }, [])

    const persistPlaybackState = useCallback(
        (overrides = {}) => {
            if (!persistPlayback || !mediaKey) return null

            const previousSnapshot = getMediaPlaybackState(mediaKey)
            return setMediaPlaybackState(mediaKey, {
                currentTime:
                    overrides.currentTime ??
                    getSafePlaybackTime(videoRef.current, previousSnapshot?.currentTime ?? 0),
            })
        },
        [mediaKey, persistPlayback, videoRef],
    )

    const restoreStoredCurrentTime = useCallback(
        videoEl => {
            if (!persistPlayback || !videoEl || !mediaKey) return

            const stored = getMediaPlaybackState(mediaKey)
            if (stored?.currentTime <= 0.05) return

            try {
                if (Math.abs((videoEl.currentTime || 0) - stored.currentTime) > 0.05) {
                    videoEl.currentTime = stored.currentTime
                }
            } catch {}
        },
        [mediaKey, persistPlayback],
    )

    useEffect(() => {
        if (!persistPlayback) return
        if (!mediaKey) return

        return subscribeToMediaPlaybackState(mediaKey, nextState => {
            const videoEl = videoRef.current
            if (!videoEl || !videoEl.paused || videoEl.ended) return
            if (nextState?.currentTime == null) return

            try {
                if (Math.abs((videoEl.currentTime || 0) - nextState.currentTime) > 0.05) {
                    videoEl.currentTime = nextState.currentTime
                }
            } catch {}
        })
    }, [mediaKey, persistPlayback, videoRef])

    useEffect(() => {
        const videoEl = videoRef.current
        if (!persistPlayback || !mediaKey || !videoEl) return

        const syncPlaybackState = () => {
            persistPlaybackState()
        }

        videoEl.addEventListener('timeupdate', syncPlaybackState)
        videoEl.addEventListener('seeked', syncPlaybackState)
        videoEl.addEventListener('pause', syncPlaybackState)
        videoEl.addEventListener('playing', syncPlaybackState)

        return () => {
            syncPlaybackState()
            videoEl.removeEventListener('timeupdate', syncPlaybackState)
            videoEl.removeEventListener('seeked', syncPlaybackState)
            videoEl.removeEventListener('pause', syncPlaybackState)
            videoEl.removeEventListener('playing', syncPlaybackState)
        }
    }, [mediaKey, persistPlayback, persistPlaybackState, videoRef])

    const handleVideoLoadedMetadata = useCallback(
        ev => {
            restoreStoredCurrentTime(ev.currentTarget)
            persistPlaybackState({ currentTime: getSafePlaybackTime(ev.currentTarget) })
        },
        [persistPlaybackState, restoreStoredCurrentTime],
    )

    const handleVideoPlay = useCallback(
        ev => {
            syncMediaPlaybackOwner(ownerIdRef.current, mediaKey, ev.currentTarget)
            claimMediaPlayback(ownerIdRef.current)
            persistPlaybackState({ currentTime: getSafePlaybackTime(ev.currentTarget) })
        },
        [persistPlaybackState],
    )

    const handleVideoPause = useCallback(
        ev => {
            releaseMediaPlayback(ownerIdRef.current)
            persistPlaybackState({ currentTime: getSafePlaybackTime(ev.currentTarget) })
        },
        [mediaKey, persistPlaybackState],
    )

    const handleVideoEnded = useCallback(
        ev => {
            releaseMediaPlayback(ownerIdRef.current, { ended: true, mediaKey })
            try {
                ev.currentTarget.currentTime = 0
            } catch {}
            persistPlaybackState({ currentTime: 0 })
        },
        [mediaKey, persistPlaybackState],
    )

    const handleVideoError = useCallback(() => {
        releaseMediaPlayback(ownerIdRef.current)
        persistPlaybackState()
    }, [persistPlaybackState])

    return {
        handleVideoEnded,
        handleVideoError,
        handleVideoLoadedMetadata,
        handleVideoPause,
        handleVideoPlay,
    }
}

const getAnswerAttachmentState = answer => {
    const extractedUrls = extractUrls(answer?.delta_json)

    return answerModel.getComposeAttachmentState({
        answer,
        extractedUrls,
        hiddenPreviewUrls: [],
        resolveEmbedAnswer: embed => cache.getCachedAnswer(embed?.id) || embed,
    })
}

const getResolvedEmbedAnswers = attachmentState =>
    (attachmentState.embeds || []).map(embed => cache.getCachedAnswer(embed?.id) || embed)

function EmbeddedPlayAnswer({
    answer,
    autoPlayMedia = false,
    isPaused = false,
    audioResumePlaybackToken = null,
    onTrackedMediaReady = undefined,
    onTrackedMediaError = undefined,
    onTrackedMediaProgressChange = undefined,
    onTrackedMediaPlaybackChange = undefined,
}) {
    const resolvedAnswer = useCachedAnswer(String(answer?.id || '')) || answer
    const media = useMemo(() => getAnswerPlayMedia(resolvedAnswer), [resolvedAnswer])
    const extractedUrls = useMemo(
        () => extractUrls(resolvedAnswer?.delta_json),
        [resolvedAnswer?.delta_json],
    )
    const attachmentState = useMemo(
        () => getAnswerAttachmentState(resolvedAnswer),
        [resolvedAnswer],
    )
    const caption = useMemo(
        () =>
            stripEmbeddedLinksFromCaption(
                getText(resolvedAnswer?.delta_json || {})?.trim() || '',
                attachmentState.embeds?.length ? extractedUrls : null,
            ),
        [attachmentState.embeds?.length, extractedUrls, resolvedAnswer?.delta_json],
    )
    const authorName =
        resolvedAnswer?.virtual_user_name ||
        resolvedAnswer?.user?.name ||
        resolvedAnswer?.user?.username ||
        'Unknown'
    const timeLabel = useMemo(
        () => formatStoryTime(resolvedAnswer?.created_at),
        [resolvedAnswer?.created_at],
    )
    const videoRef = useRef(null)
    const videoProgressRafRef = useRef(0)
    const [showVideoControls, setShowVideoControls] = useState(false)
    const {
        handleVideoEnded,
        handleVideoError,
        handleVideoLoadedMetadata,
        handleVideoPause,
        handleVideoPlay,
    } = usePlaySlideVideoPlayback(resolvedAnswer?.id, media.src, media.mp4Src, videoRef, false)

    const stopPropagationOnly = useCallback(ev => {
        ev?.stopPropagation?.()
    }, [])

    const stopVideoProgressLoop = useCallback(() => {
        if (videoProgressRafRef.current) {
            window.cancelAnimationFrame(videoProgressRafRef.current)
            videoProgressRafRef.current = 0
        }
    }, [])

    const startVideoProgressLoop = useCallback(() => {
        if (!autoPlayMedia) return

        stopVideoProgressLoop()

        const updateProgress = () => {
            const video = videoRef.current
            if (!video || video.paused || video.ended) {
                videoProgressRafRef.current = 0
                return
            }

            onTrackedMediaProgressChange?.(video.currentTime, video.duration)
            videoProgressRafRef.current = window.requestAnimationFrame(updateProgress)
        }

        videoProgressRafRef.current = window.requestAnimationFrame(updateProgress)
    }, [autoPlayMedia, onTrackedMediaProgressChange, stopVideoProgressLoop])

    useEffect(() => {
        return () => {
            stopVideoProgressLoop()
        }
    }, [stopVideoProgressLoop])

    useEffect(() => {
        if (media.kind !== 'video') return

        const video = videoRef.current
        if (!video) return

        if (isPaused) {
            if (!video.paused) video.pause()
            return
        }

        if (!autoPlayMedia) return

        video.play().catch(error => {
            if (error?.name === 'NotAllowedError') {
                setShowVideoControls(true)
                return
            }

            console.warn('Failed to autoplay embedded story video:', error)
        })
    }, [autoPlayMedia, isPaused, media.kind, media.mp4Src, media.src])

    const renderEmbeddedMedia = () => {
        switch (media.kind) {
            case 'image':
                return (
                    <div className={styles.embeddedVisualMedia}>
                        <img
                            className={styles.embeddedImageMedia}
                            src={media.src}
                            alt={caption || authorName}
                            draggable={false}
                        />
                    </div>
                )
            case 'video':
                return (
                    <div className={styles.embeddedVisualMedia}>
                        <video
                            ref={videoRef}
                            className={styles.embeddedVideoMedia}
                            playsInline
                            preload="metadata"
                            poster={media.poster || undefined}
                            controls={showVideoControls || !autoPlayMedia}
                            onClick={stopPropagationOnly}
                            onLoadedMetadata={ev => {
                                handleVideoLoadedMetadata(ev)
                                if (autoPlayMedia) {
                                    onTrackedMediaReady?.(ev.currentTarget.duration)
                                }
                            }}
                            onPlay={ev => {
                                handleVideoPlay(ev)
                                onTrackedMediaPlaybackChange?.(true)
                                startVideoProgressLoop()
                                if (autoPlayMedia && showVideoControls) {
                                    onTrackedMediaReady?.(videoRef.current?.duration)
                                }
                            }}
                            onPause={ev => {
                                handleVideoPause(ev)
                                stopVideoProgressLoop()
                                onTrackedMediaPlaybackChange?.(false)
                            }}
                            onTimeUpdate={ev => {
                                onTrackedMediaProgressChange?.(
                                    ev.currentTarget.currentTime,
                                    ev.currentTarget.duration,
                                )
                            }}
                            onEnded={ev => {
                                stopVideoProgressLoop()
                                onTrackedMediaProgressChange?.(
                                    ev.currentTarget.duration,
                                    ev.currentTarget.duration,
                                )
                                onTrackedMediaPlaybackChange?.(false)
                                handleVideoEnded(ev)
                            }}
                            onError={() => {
                                stopVideoProgressLoop()
                                handleVideoError()
                                if (autoPlayMedia) {
                                    onTrackedMediaError?.()
                                }
                            }}
                        >
                            {media.mp4Src && <source src={media.mp4Src} type="video/mp4" />}
                            {media.src && (
                                <source src={media.src} type={media.srcType || 'video/webm'} />
                            )}
                        </video>
                    </div>
                )
            case 'image-audio':
                return (
                    <div className={styles.embeddedImageAudioStack}>
                        <div className={styles.embeddedVisualMedia}>
                            <img
                                className={styles.embeddedImageMedia}
                                src={media.imageSrc}
                                alt={caption || authorName}
                                draggable={false}
                            />
                        </div>

                        <div
                            className={styles.embeddedAudio}
                            data-no-modal-interaction
                            data-story-control-exempt="true"
                        >
                            <FileEmbed
                                key={media.file?.url}
                                file={media.file}
                                answerId={resolvedAnswer?.id}
                                audioTheme="play"
                                audioArtwork={false}
                                audioAutoPlay={false}
                                audioIsPaused={isPaused}
                                audioPersistPlayback={false}
                                audioResumePlaybackToken={
                                    autoPlayMedia ? audioResumePlaybackToken : undefined
                                }
                                audioOnDurationChange={
                                    autoPlayMedia ? onTrackedMediaReady : undefined
                                }
                                audioOnProgressChange={
                                    autoPlayMedia ? onTrackedMediaProgressChange : undefined
                                }
                                audioOnPlaybackError={
                                    autoPlayMedia ? onTrackedMediaError : undefined
                                }
                                audioOnPlaybackStateChange={
                                    autoPlayMedia ? onTrackedMediaPlaybackChange : undefined
                                }
                            />
                        </div>
                    </div>
                )
            case 'audio':
                return (
                    <div
                        className={styles.embeddedAudio}
                        data-no-modal-interaction
                        data-story-control-exempt="true"
                    >
                        <FileEmbed
                            key={media.file?.url}
                            file={media.file}
                            answerId={resolvedAnswer?.id}
                            audioTheme="play"
                            audioAutoPlay={false}
                            audioIsPaused={isPaused}
                            audioPersistPlayback={false}
                            audioResumePlaybackToken={
                                autoPlayMedia ? audioResumePlaybackToken : undefined
                            }
                            audioOnDurationChange={autoPlayMedia ? onTrackedMediaReady : undefined}
                            audioOnProgressChange={
                                autoPlayMedia ? onTrackedMediaProgressChange : undefined
                            }
                            audioOnPlaybackError={autoPlayMedia ? onTrackedMediaError : undefined}
                            audioOnPlaybackStateChange={
                                autoPlayMedia ? onTrackedMediaPlaybackChange : undefined
                            }
                        />
                    </div>
                )
            case 'file':
                return (
                    <div
                        className={cn(styles.storyCard, styles.embeddedCardBody)}
                        data-no-modal-interaction
                        data-story-control-exempt="true"
                    >
                        <FileEmbed
                            key={media.file?.url}
                            file={media.file}
                            answerId={resolvedAnswer?.id}
                        />
                    </div>
                )
            case 'link':
                return null
            case 'text':
            default:
                return null
        }
    }

    const embeddedContent = renderEmbeddedMedia()
    const showCaption = Boolean(caption)
    const showPlainLink = media.kind === 'link' && media.url

    if (!embeddedContent && !showCaption && !showPlainLink) return null

    return (
        <div className={styles.embeddedAnswer}>
            <div className={styles.embeddedAnswerHeader}>
                <UserIcon
                    user={resolvedAnswer?.user}
                    size="small"
                    virtualUserIcon={resolvedAnswer?.virtual_user_icon_url}
                    contextStyles={styles.embeddedAuthorAvatar}
                />

                <div className={styles.embeddedAuthorText}>
                    <div className={styles.embeddedAuthorName}>{authorName}</div>
                    {timeLabel && <div className={styles.embeddedAuthorSubline}>{timeLabel}</div>}
                </div>
            </div>

            <div className={styles.embeddedAnswerBody}>
                {embeddedContent}
                {showCaption && <div className={styles.embeddedText}>{caption}</div>}
                {showPlainLink && <div className={styles.embeddedLinkText}>{media.url}</div>}
            </div>
        </div>
    )
}

function FloatingActionDock({
    answer,
    quest,
    onOpenReactionPicker,
    onCloseReactionPicker,
    onReactionCommitted,
}) {
    const stopInteractiveEvent = useCallback(ev => {
        ev?.stopPropagation?.()
    }, [])

    if (!answer || answer?.is_system) return null

    return (
        <div
            className={styles.storyActionDock}
            data-no-modal-interaction
            data-story-control-exempt="true"
            onClick={stopInteractiveEvent}
            onMouseDown={stopInteractiveEvent}
            onMouseUp={stopInteractiveEvent}
            onPointerDown={stopInteractiveEvent}
            onTouchStart={stopInteractiveEvent}
            onTouchEnd={stopInteractiveEvent}
        >
            <div className={styles.storyActionDockInner}>
                <AnswerButtons
                    answer={answer}
                    quest={quest}
                    highlight={false}
                    theme="play"
                    thin
                    className={styles.storyActionControls}
                    onOpenReactionPicker={onOpenReactionPicker}
                    onCloseReactionPicker={onCloseReactionPicker}
                    onReactionCommitted={onReactionCommitted}
                />
            </div>
        </div>
    )
}

export default function PlayStorySlide(props) {
    const { story, action, isPaused, messageHandler } = props
    const storyAnswer = story?.answer
    const answer = useCachedAnswer(String(storyAnswer?.id || '')) || storyAnswer
    const media = story?.media || { kind: 'text' }
    const {
        setCount: setStoryCount,
        setPause: setStoryPause,
        setProgressMode,
        setVideoDuration: setStoryDuration,
        playbackCommand,
    } = useContext(ProgressContext)
    const videoRef = useRef(null)
    const actionRef = useRef(action)
    const messageHandlerRef = useRef(messageHandler)
    const videoProgressRafRef = useRef(0)
    const durationResolvedRef = useRef(false)
    const trackedPlaybackStartedRef = useRef(false)
    const trackedPlaybackStateRef = useRef(null)
    const handledStoryControlResumeTokenRef = useRef(null)
    const reactionPickerPausedPlaybackRef = useRef(false)
    const [trackedAudioPlayRequestToken, setTrackedAudioPlayRequestToken] = useState(0)
    const [showVideoControls, setShowVideoControls] = useState(false)
    const [videoReady, setVideoReady] = useState(media.kind !== 'video')
    const {
        handleVideoEnded,
        handleVideoError,
        handleVideoLoadedMetadata,
        handleVideoPause,
        handleVideoPlay,
    } = usePlaySlideVideoPlayback(answer?.id, media.src, media.mp4Src, videoRef, false)
    const extractedUrls = useMemo(() => extractUrls(answer?.delta_json), [answer?.delta_json])
    const attachmentState = useMemo(() => getAnswerAttachmentState(answer), [answer])

    const caption = useMemo(
        () =>
            stripEmbeddedLinksFromCaption(
                getText(answer?.delta_json || {})?.trim() || '',
                attachmentState.embeds?.length ? extractedUrls : null,
            ),
        [answer?.delta_json, attachmentState.embeds?.length, extractedUrls],
    )
    const authorName =
        answer?.virtual_user_name || answer?.user?.name || answer?.user?.username || 'Unknown'
    const timeLabel = useMemo(() => formatStoryTime(answer?.created_at), [answer?.created_at])
    const backdropUrl = useMemo(() => getMediaBackdropUrl(media), [media])
    const hasCaption = Boolean(caption && media.kind !== 'text')
    const embedAnswers = useMemo(() => getResolvedEmbedAnswers(attachmentState), [attachmentState])
    const trackedEmbed = useMemo(() => {
        if (media.kind !== 'text') return null

        for (const embed of embedAnswers) {
            const embedMedia = getAnswerPlayMedia(embed)
            if (isTrackedPlayMedia(embedMedia)) {
                return embed
            }
        }

        return null
    }, [embedAnswers, media.kind])
    const shouldTrackEmbeddedMedia = Boolean(trackedEmbed)
    const trackedMediaKind = useMemo(
        () => getTrackedMediaKind(media.kind, getAnswerPlayMedia(trackedEmbed)?.kind),
        [media.kind, trackedEmbed],
    )
    const storyControlResumeToken = getStoryControlResumeToken(playbackCommand)
    const trackedAudioResumePlaybackToken = getTrackedAudioResumePlaybackToken(
        trackedMediaKind,
        trackedAudioPlayRequestToken,
        storyControlResumeToken,
    )

    useEffect(() => {
        actionRef.current = action
    }, [action])

    useEffect(() => {
        messageHandlerRef.current = messageHandler
    }, [messageHandler])

    useEffect(() => {
        reactionPickerPausedPlaybackRef.current = false
    }, [story?.id])

    const stopVideoProgressLoop = useCallback(() => {
        if (videoProgressRafRef.current) {
            window.cancelAnimationFrame(videoProgressRafRef.current)
            videoProgressRafRef.current = 0
        }
    }, [])

    const stopPropagationOnly = useCallback(ev => {
        ev?.stopPropagation?.()
    }, [])

    const handleReactionPickerOpen = useCallback(() => {
        const nextState = getReactionPickerOpenBehavior(isPaused)
        reactionPickerPausedPlaybackRef.current = nextState.reactionPickerPausedPlayback

        if (nextState.shouldPausePlayback) {
            actionRef.current?.('pause', 'story-control')
        }
    }, [isPaused])

    const handleReactionPickerClose = useCallback(() => {
        const nextState = getReactionPickerCloseBehavior(reactionPickerPausedPlaybackRef.current)
        reactionPickerPausedPlaybackRef.current = nextState.reactionPickerPausedPlayback

        if (nextState.shouldResumePlayback) {
            actionRef.current?.('play', 'story-control')
        }
    }, [])

    const reportTrackedDuration = useCallback(
        duration => {
            if (!Number.isFinite(duration) || duration <= 0) return
            if (setStoryDuration) {
                setStoryDuration(duration * 1000)
                return
            }

            messageHandlerRef.current?.('UPDATE_VIDEO_DURATION', { duration })
        },
        [setStoryDuration],
    )

    const resetTrackedDuration = useCallback(() => {
        if (setStoryDuration) {
            setStoryDuration(0)
            return
        }

        messageHandlerRef.current?.('UPDATE_VIDEO_DURATION', { duration: 0 })
    }, [setStoryDuration])

    const syncTrackedProgress = useCallback(
        (currentTime, duration) => {
            if (!Number.isFinite(currentTime) || currentTime < 0) return
            if (!Number.isFinite(duration) || duration <= 0) return
            const progress = Math.max(0, Math.min(100, (currentTime / duration) * 100))

            if (setStoryCount) {
                setStoryCount(progress)
                return
            }

            messageHandlerRef.current?.('SYNC_PLAY_PROGRESS', {
                progress,
            })
        },
        [setStoryCount],
    )

    const syncTrackedPause = useCallback(
        isPlaying => {
            if (setStoryPause) {
                setStoryPause(!isPlaying)
                return
            }

            actionRef.current(isPlaying ? 'play' : 'pause')
        },
        [setStoryPause],
    )

    const startVideoProgressLoop = useCallback(() => {
        stopVideoProgressLoop()

        const updateProgress = () => {
            const video = videoRef.current
            if (!video || video.paused || video.ended) {
                videoProgressRafRef.current = 0
                return
            }

            syncTrackedProgress(video.currentTime, video.duration)
            videoProgressRafRef.current = window.requestAnimationFrame(updateProgress)
        }

        videoProgressRafRef.current = window.requestAnimationFrame(updateProgress)
    }, [stopVideoProgressLoop, syncTrackedProgress])

    useEffect(() => {
        if (!story?.trackDuration) return
        durationResolvedRef.current = false
        trackedPlaybackStartedRef.current = false
        trackedPlaybackStateRef.current = null
        handledStoryControlResumeTokenRef.current = null
        setTrackedAudioPlayRequestToken(0)
        resetTrackedDuration()
        setProgressMode?.('media')
    }, [resetTrackedDuration, setProgressMode, story?.duration, story?.id, story?.trackDuration])

    useEffect(() => {
        if (!shouldTrackEmbeddedMedia) return
        durationResolvedRef.current = false
        trackedPlaybackStartedRef.current = false
        trackedPlaybackStateRef.current = null
        handledStoryControlResumeTokenRef.current = null
        setTrackedAudioPlayRequestToken(0)
        resetTrackedDuration()
        setProgressMode?.('media')
    }, [resetTrackedDuration, setProgressMode, shouldTrackEmbeddedMedia, story?.id])

    useEffect(() => {
        if (
            storyControlResumeToken == null ||
            handledStoryControlResumeTokenRef.current === storyControlResumeToken
        ) {
            return
        }

        handledStoryControlResumeTokenRef.current = storyControlResumeToken

        if (isTrackedAudioMediaKind(trackedMediaKind)) {
            setTrackedAudioPlayRequestToken(storyControlResumeToken)
        }
    }, [storyControlResumeToken, trackedMediaKind])

    useEffect(() => {
        setShowVideoControls(false)
        setVideoReady(media.kind !== 'video')
    }, [media.kind, media.src])

    useEffect(() => {
        return () => {
            stopVideoProgressLoop()
        }
    }, [stopVideoProgressLoop])

    useEffect(() => {
        if (media.kind !== 'video' || !videoReady) return

        const video = videoRef.current
        if (!video) return

        if (isPaused) {
            if (!video.paused) video.pause()
            return
        }

        video.play().catch(error => {
            if (error?.name === 'NotAllowedError') {
                setShowVideoControls(true)
                actionRef.current('pause')
                return
            }

            console.warn('Failed to autoplay story video:', error)
        })
    }, [isPaused, media.kind, videoReady])

    const handleTrackedMediaReady = useCallback(
        duration => {
            const shouldStartTrackedPlayback =
                !durationResolvedRef.current && !trackedPlaybackStartedRef.current

            reportTrackedDuration(duration)
            setProgressMode?.('media')
            durationResolvedRef.current = true
            if (!shouldStartTrackedPlayback || isPaused) return

            if (isTrackedAudioMediaKind(trackedMediaKind)) {
                setTrackedAudioPlayRequestToken(token => token + 1)
                return
            }

            syncTrackedPause(true)
        },
        [isPaused, reportTrackedDuration, setProgressMode, syncTrackedPause, trackedMediaKind],
    )

    const handleTrackedMediaError = useCallback(() => {
        if (durationResolvedRef.current) return
        durationResolvedRef.current = true
        reportTrackedDuration((story?.duration || 6500) / 1000)
        setProgressMode?.('timer')
    }, [reportTrackedDuration, setProgressMode, story?.duration])

    const handleTrackedMediaPlaybackChange = useCallback(
        isPlaying => {
            if (trackedPlaybackStateRef.current === isPlaying) return
            trackedPlaybackStateRef.current = isPlaying

            if (isPlaying) {
                trackedPlaybackStartedRef.current = true
                setProgressMode?.('media')
            }

            syncTrackedPause(isPlaying)
        },
        [setProgressMode, syncTrackedPause],
    )

    const renderMedia = () => {
        switch (media.kind) {
            case 'image':
                return (
                    <div className={styles.visualMedia}>
                        <img
                            className={styles.imageMedia}
                            src={media.src}
                            alt={caption || authorName}
                            draggable={false}
                        />
                    </div>
                )
            case 'image-audio':
                return (
                    <div className={styles.imageAudioStack}>
                        <div className={styles.visualMedia}>
                            <img
                                className={styles.imageMedia}
                                src={media.imageSrc}
                                alt={caption || authorName}
                                draggable={false}
                            />
                        </div>

                        <div className={styles.inlineAudio}>
                            <FileEmbed
                                key={media.file?.url}
                                file={media.file}
                                answerId={answer?.id}
                                audioTheme="play"
                                audioArtwork={false}
                                audioAutoPlay={false}
                                audioIsPaused={isPaused}
                                audioPersistPlayback={false}
                                audioResumePlaybackToken={trackedAudioResumePlaybackToken}
                                audioOnDurationChange={handleTrackedMediaReady}
                                audioOnProgressChange={syncTrackedProgress}
                                audioOnPlaybackError={handleTrackedMediaError}
                                audioOnPlaybackStateChange={handleTrackedMediaPlaybackChange}
                            />
                        </div>
                    </div>
                )
            case 'video':
                return (
                    <div
                        className={cn(
                            styles.visualMedia,
                            showVideoControls && styles.visualMediaInteractive,
                        )}
                    >
                        <video
                            ref={videoRef}
                            className={styles.videoMedia}
                            playsInline
                            preload="metadata"
                            poster={media.poster || undefined}
                            controls={showVideoControls}
                            onClick={stopPropagationOnly}
                            onLoadedMetadata={ev => {
                                handleVideoLoadedMetadata(ev)
                                setVideoReady(true)
                                handleTrackedMediaReady(ev.currentTarget.duration)
                            }}
                            onPlay={ev => {
                                handleVideoPlay(ev)
                                handleTrackedMediaPlaybackChange(true)
                                startVideoProgressLoop()
                                if (showVideoControls) actionRef.current('play')
                            }}
                            onPause={ev => {
                                handleVideoPause(ev)
                                handleTrackedMediaPlaybackChange(false)
                                stopVideoProgressLoop()
                                if (showVideoControls) actionRef.current('pause')
                            }}
                            onTimeUpdate={ev => {
                                syncTrackedProgress(
                                    ev.currentTarget.currentTime,
                                    ev.currentTarget.duration,
                                )
                            }}
                            onEnded={ev => {
                                stopVideoProgressLoop()
                                handleVideoEnded(ev)
                            }}
                            onError={() => {
                                stopVideoProgressLoop()
                                handleVideoError()
                                handleTrackedMediaError()
                            }}
                        >
                            {media.mp4Src && <source src={media.mp4Src} type="video/mp4" />}
                            {media.src && (
                                <source src={media.src} type={media.srcType || 'video/webm'} />
                            )}
                        </video>
                    </div>
                )
            case 'audio':
                return (
                    <div className={styles.inlineAudio}>
                        <FileEmbed
                            key={media.file?.url}
                            file={media.file}
                            answerId={answer?.id}
                            audioTheme="play"
                            audioAutoPlay={false}
                            audioIsPaused={isPaused}
                            audioPersistPlayback={false}
                            audioResumePlaybackToken={trackedAudioResumePlaybackToken}
                            audioOnDurationChange={handleTrackedMediaReady}
                            audioOnProgressChange={syncTrackedProgress}
                            audioOnPlaybackError={handleTrackedMediaError}
                            audioOnPlaybackStateChange={handleTrackedMediaPlaybackChange}
                        />
                    </div>
                )
            case 'file':
                return (
                    <div className={cn(styles.storyCard, styles.fileStoryCard)}>
                        <FileEmbed key={media.file?.url} file={media.file} answerId={answer?.id} />
                    </div>
                )
            case 'link':
                return (
                    <div className={cn(styles.storyCard, styles.linkStoryCard)}>
                        <LinkPreview
                            url={media.url}
                            urlModel={answer?.url || null}
                            answerId={answer?.id}
                        />
                    </div>
                )
            case 'text':
            default:
                if (!caption) return null

                return (
                    <div className={styles.textStoryContent}>
                        <div className={styles.textOnlyBody}>{caption}</div>
                    </div>
                )
        }
    }

    const mediaContent = renderMedia()

    return (
        <div className={styles.storySurface}>
            {backdropUrl && (
                <div
                    className={styles.storyBackdrop}
                    style={{ backgroundImage: `url(${backdropUrl})` }}
                    aria-hidden="true"
                />
            )}

            <div className={styles.storyGradient} aria-hidden="true" />

            <div className={styles.storyFrame}>
                <div className={styles.storyHeader}>
                    <div className={styles.authorMeta}>
                        <UserIcon
                            user={answer?.user}
                            size="medium"
                            virtualUserIcon={answer?.virtual_user_icon_url}
                            contextStyles={styles.authorAvatar}
                        />

                        <div className={styles.authorText}>
                            <div className={styles.authorName}>{authorName}</div>
                            {timeLabel && <div className={styles.authorSubline}>{timeLabel}</div>}
                        </div>
                    </div>
                </div>

                <div className={styles.storyBody}>
                    {mediaContent && <div className={styles.mediaShell}>{mediaContent}</div>}

                    {hasCaption && <div className={styles.caption}>{caption}</div>}

                    {!!embedAnswers.length && (
                        <div className={styles.embeddedAnswers}>
                            {embedAnswers.map(embed => (
                                <EmbeddedPlayAnswer
                                    key={embed?.id || embed?.child_quests?.[0]?.id}
                                    answer={embed}
                                    autoPlayMedia={
                                        String(embed?.id || '') === String(trackedEmbed?.id || '')
                                    }
                                    isPaused={isPaused}
                                    audioResumePlaybackToken={trackedAudioResumePlaybackToken}
                                    onTrackedMediaReady={handleTrackedMediaReady}
                                    onTrackedMediaError={handleTrackedMediaError}
                                    onTrackedMediaProgressChange={syncTrackedProgress}
                                    onTrackedMediaPlaybackChange={handleTrackedMediaPlaybackChange}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <FloatingActionDock
                answer={answer}
                quest={story?.quest}
                onOpenReactionPicker={handleReactionPickerOpen}
                onCloseReactionPicker={handleReactionPickerClose}
                onReactionCommitted={handleReactionPickerClose}
            />
        </div>
    )
}
